/**
 * AnimatedBeamModal
 *
 * Layout: left = person image, middle = "Continuous Medical Record" (hub),
 * right = numbers 1–7 in a semicircle centered on the hub. Beams from 1–7 to hub,
 * and hub ↔ person in both directions.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   personImage: string — optional URL for the left circle (person).
 *   images: string[] — optional image URLs for circles 1–7 (indices 0–6).
 *   title: string
 */

import React, { forwardRef, useRef, useMemo, useState, useLayoutEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AnimatedBeam from "./AnimatedBeam";
import CaduceusIcon from "./CaduceusIcon";

const CIRCLE_SIZE = 48;   // number circles (right column)
const HUB_SIZE = 86;      // center circle (20% larger than 72)
const PERSON_SIZE = 72;   // left (person) circle
const _CMR_SIZE = HUB_SIZE;
const ARC_RADIUS = 130; // radius of semicircle for 1–7 (px)
// 0–6: number→hub (0 = straight so beams don’t cross); 7: hub–person (slight curve)
const BEAM_CURVATURES = [0, 0, 0, 0, 0, 0, 0, 12];
const _beamCurvatures = BEAM_CURVATURES;

function getDefaultArcPositions(n, circleSize) {
  const cx = 180;
  const cy = 220;
  const r = ARC_RADIUS;
  const half = circleSize / 2;
  return Array.from({ length: n }, (_, i) => {
    const angleDeg = -90 + (i * 180) / (n - 1);
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
      left: cx + r * Math.cos(angleRad) - half,
      top: cy + r * Math.sin(angleRad) - half,
    };
  });
}

const circleSx = {
  borderRadius: "50%",
  border: "2px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  boxShadow: "0 0 20px -12px rgba(0,0,0,0.8)",
  overflow: "hidden",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const Circle = forwardRef(function Circle({ size = CIRCLE_SIZE, children, ...rest }, ref) {
  return (
    <Box
      ref={ref}
      sx={{
        ...circleSx,
        width: size,
        height: size,
        zIndex: 10,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
});

export default function AnimatedBeamModal({
  open,
  onClose,
  personImage,
  images = [],
  title = (
    <>
      Enhanced Connections Throughout Healthcare
      <br />
      Medical SuperIntelligence³
    </>
  ),
}) {
  const containerRef = useRef(null);
  const personRef = useRef(null);
  const hubRef = useRef(null);
  const div1Ref = useRef(null);
  const div2Ref = useRef(null);
  const div3Ref = useRef(null);
  const div4Ref = useRef(null);
  const div5Ref = useRef(null);
  const div6Ref = useRef(null);
  const div7Ref = useRef(null);

  const numberRefs = [div1Ref, div2Ref, div3Ref, div4Ref, div5Ref, div6Ref, div7Ref];

  const [arcPositions, setArcPositions] = useState(() => getDefaultArcPositions(7, CIRCLE_SIZE));

  useLayoutEffect(() => {
    if (!open || !containerRef.current || !hubRef.current) return;
    const container = containerRef.current;
    const hub = hubRef.current;
    const cRect = container.getBoundingClientRect();
    const hubRect = hub.getBoundingClientRect();
    const hubCenterX = hubRect.left - cRect.left + hubRect.width / 2;
    const hubCenterY = hubRect.top - cRect.top + hubRect.height / 2;
    const r = ARC_RADIUS;
    const half = CIRCLE_SIZE / 2;
    const positions = Array.from({ length: 7 }, (_, i) => {
      const angleDeg = -90 + (i * 180) / 6;
      const angleRad = (angleDeg * Math.PI) / 180;
      return {
        left: hubCenterX + r * Math.cos(angleRad) - half,
        top: hubCenterY + r * Math.sin(angleRad) - half,
      };
    });
    setArcPositions(positions);
  }, [open]);

  // Random per beam when modal opens: direction (reverse), delay, and duration.
  // Shuffle 4 reverse / 4 normal so travellers are guaranteed to go in both directions.
  const beamConfig = useMemo(() => {
    const directions = [true, true, true, true, false, false, false, false];
    for (let i = directions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [directions[i], directions[j]] = [directions[j], directions[i]];
    }
    return [0, 1, 2, 3, 4, 5, 6, 7].map((i) => ({
      reverse: directions[i],
      delay: Math.random() * 2.2,
      duration: 2.2 + Math.random() * 1.4,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Person–hub: several beams with random delays and mixed direction so travellers go both ways at random intervals (can cross).
  const personHubBeamCount = 6;
  const personHubBeamConfig = useMemo(() => {
    const directions = [true, true, true, false, false, false];
    for (let i = directions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [directions[i], directions[j]] = [directions[j], directions[i]];
    }
    return Array.from({ length: personHubBeamCount }, (_, i) => ({
      reverse: directions[i],
      delay: Math.random() * 4,
      duration: 2 + Math.random() * 2,
      fromHub: Math.random() > 0.5,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          maxHeight: "88vh",
          maxWidth: 675,
        },
      }}
    >
      <DialogTitle
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" component="span" sx={{ color: "primary.main", textAlign: "center", fontWeight: "bold" }}>
          {title}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <Box
          ref={containerRef}
          sx={{
            position: "relative",
            width: "100%",
            minHeight: 400,
            flex: "0 0 auto",
            overflow: "visible",
            p: 3,
          }}
        >
          {/* Soft gradient background */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              background: "radial-gradient(ellipse 80% 80% at 50% 50%, rgba(27, 69, 132, 0.12) 0%, rgba(17, 122, 202, 0.06) 50%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          {/* Center circle (hub) – truly centered */}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
            }}
          >
            <Circle ref={hubRef} size={HUB_SIZE}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                  textAlign: "center",
                  px: 0.5,
                  lineHeight: 1.2,
                  fontSize: "0.78rem",
                }}
              >
                Continuous Medical Record
              </Typography>
            </Circle>
          </Box>

          {/* Person circle – left of center */}
          <Box
            sx={{
              position: "absolute",
              left: 88,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
            }}
          >
            <Circle ref={personRef} size={PERSON_SIZE}>
              {personImage && typeof personImage === "string" ? (
                <Box
                  component="img"
                  src={personImage}
                  alt="Person"
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <PersonIcon sx={{ fontSize: 42, color: "primary.main" }} />
              )}
            </Circle>
          </Box>

          {/* Numbers 1–7 along semicircle – visible circles with white fill and blue border */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              overflow: "visible",
              pointerEvents: "none",
              zIndex: 10,
            }}
          >
            <Box sx={{ position: "absolute", inset: 0, overflow: "visible" }}>
              {[1, 2, 3, 4, 5, 6, 7].map((n, i) => (
                <Circle
                  key={n}
                  ref={numberRefs[i]}
                  size={n === 1 ? 50 : CIRCLE_SIZE}
                  sx={{
                    position: "absolute",
                    left: (arcPositions[i]?.left ?? 0) - (n === 1 ? 1 : 0),
                    top: (arcPositions[i]?.top ?? 0) - (n <= 4 ? 4 : 0),
                    width: n === 1 ? 50 : 48,
                    height: n <= 4 ? 56 : CIRCLE_SIZE,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "auto",
                    backgroundColor: "common.white",
                    border: "3px solid #1b4584",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    zIndex: 15,
                  }}
                >
                  {images[i] && typeof images[i] === "string" ? (
                    <Box
                      component="img"
                      src={images[i]}
                      alt={`${n}`}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : n <= 4 ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, pt: 2, pb: 2 }}>
                      <CaduceusIcon sx={{ fontSize: 31, color: "primary.main" }} />
                      <Typography component="span" sx={{ fontWeight: 700, color: "primary.main", fontSize: "0.75rem" }}>
                        {n}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
                      <LocalHospitalIcon sx={{ fontSize: 26, color: "primary.main" }} />
                      <Typography component="span" sx={{ fontWeight: 700, color: "primary.main", fontSize: "0.75rem" }}>
                        {n}
                      </Typography>
                    </Box>
                  )}
                </Circle>
              ))}
            </Box>
          </Box>

          {/* Animated beams (curved): 1–7 → hub; then hub ↔ person in both directions */}
          {open && (
            <>
              {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                <AnimatedBeam
                  key={`beam-${i}`}
                  containerRef={containerRef}
                  fromRef={numberRefs[i]}
                  toRef={hubRef}
                  duration={beamConfig[i].duration}
                  curvature={BEAM_CURVATURES[i]}
                  reverse={beamConfig[i].reverse}
                  delay={beamConfig[i].delay}
                  open={open}
                  layoutDeps={arcPositions}
                />
              ))}
              {personHubBeamConfig.map((cfg, i) =>
                cfg.fromHub ? (
                  <AnimatedBeam
                    key={`beam-hub-person-${i}`}
                    containerRef={containerRef}
                    fromRef={hubRef}
                    toRef={personRef}
                    duration={cfg.duration}
                    curvature={BEAM_CURVATURES[7]}
                    reverse={cfg.reverse}
                    delay={cfg.delay}
                    open={open}
                    layoutDeps={arcPositions}
                    startXOffset={-HUB_SIZE / 2}
                    endXOffset={PERSON_SIZE / 2}
                  />
                ) : (
                  <AnimatedBeam
                    key={`beam-person-hub-${i}`}
                    containerRef={containerRef}
                    fromRef={personRef}
                    toRef={hubRef}
                    duration={cfg.duration}
                    curvature={BEAM_CURVATURES[7]}
                    reverse={cfg.reverse}
                    delay={cfg.delay}
                    open={open}
                    layoutDeps={arcPositions}
                    startXOffset={PERSON_SIZE / 2}
                    endXOffset={-HUB_SIZE / 2}
                  />
                )
              )}
            </>
          )}
        </Box>

        {/* Footer – prominent CMR and partner benefits */}
        <Box
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            overflow: "auto",
            px: 2.5,
            py: 2.5,
            borderTop: "2px solid",
            borderColor: "primary.main",
            backgroundColor: "rgba(27, 69, 132, 0.06)",
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Federo", sans-serif',
              color: "primary.main",
              fontSize: "1.05rem",
              lineHeight: 1.55,
              textAlign: "left",
              fontWeight: "bold",
              mb: 1.5,
            }}
          >
            The Continuous Medical Record starts with standalone benefits that expand, by design, over time.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.75 } }}>
            <Typography component="li" sx={{ fontFamily: '"Federo", sans-serif', color: "primary.main", fontSize: "1rem", lineHeight: 1.45, fontWeight: "bold" }}>
              <strong>Patients</strong> gain medication safety, symptom guidance, and portable access to their essential health records anywhere
            </Typography>
            <Typography component="li" sx={{ fontFamily: '"Federo", sans-serif', color: "primary.main", fontSize: "1rem", lineHeight: 1.45, fontWeight: "bold" }}>
              <strong>Providers</strong> benefit from the best medication lists, seamless care transitions, and tools designed to enhance clinical judgment
            </Typography>
            <Typography component="li" sx={{ fontFamily: '"Federo", sans-serif', color: "primary.main", fontSize: "1rem", lineHeight: 1.45, fontWeight: "bold" }}>
              <strong>Institutions</strong> expect to improve outcomes and productivity, reduce readmissions, and sustainably lead the way forward in healthcare.
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: '"Federo", sans-serif',
              color: "primary.main",
              fontSize: "1.05rem",
              lineHeight: 1.55,
              textAlign: "left",
              fontWeight: "bold",
              mt: 1.5,
            }}
          >
            When these three partners adopt together, they create exponential improvement in the healthcare experience. That's MSI³.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
